import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Plus, Trash2, Copy, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, useFetch } from "../lib/api";
import { money, imgUrl, UK_SIZES } from "../lib/shop";
import { AdminTitle, Toggle } from "./AdminLayout";
import { ImageManager } from "./Images";
import { ColourEditor, ColourVariants } from "./Colours";

export function ProductList() {
  const { data, reload } = useFetch("/admin/products");
  const [q, setQ] = useState("");
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const list = (data || []).filter((p) => `${p.name} ${p.brand} ${p.sku}`.toLowerCase().includes(q.toLowerCase()));
  const demoCount = (data || []).filter((p) => p.is_demo).length;
  return (
    <div>
      <AdminTitle title="Products">
        {demoCount > 0 && <button className="btn-outline !min-h-[44px] !text-red-300" data-testid="admin-delete-demo-button"
          onClick={() => window.confirm(`Delete all ${demoCount} DEMO products permanently?`) && act(() => api.delete("/admin/demo-products"), "Demo products deleted")}>Delete {demoCount} demo products</button>}
        <Link to="/admin/products/new" className="btn-gold !min-h-[44px]" data-testid="admin-add-product-button"><Plus size={16} /> Add product</Link>
      </AdminTitle>
      <input className="field max-w-sm mb-4" placeholder="Search products" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-product-search" />
      <div className="space-y-2" data-testid="admin-product-list">
        {list.map((p) => {
          const stock = p.variants.reduce((s, v) => s + v.stock, 0);
          return (
            <div key={p.id} className={`card-dark p-3 flex items-center gap-3 ${p.archived ? "opacity-50" : ""}`} data-testid={`admin-product-row-${p.slug}`}>
              <img src={imgUrl(p.images?.[0]?.url)} alt="" className="w-14 h-14 rounded-lg object-cover shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-bold truncate">{p.name} {p.is_demo && <span className="text-[10px] bg-amber-500/20 text-amber-300 px-1.5 py-0.5 rounded ml-1">DEMO</span>}</p>
                <p className="text-xs text-zinc-400">{p.brand} · {p.category} · {money(p.price)}{p.on_sale && p.sale_price ? ` → ${money(p.sale_price)}` : ""} · <span className={stock ? "" : "text-red-400"}>{stock} in stock</span></p>
                <p className="text-[11px] text-zinc-500">{p.archived ? "Archived" : p.published ? "Published" : "Unpublished"}{p.featured ? " · Featured" : ""}{p.new_arrival ? " · New" : ""}{p.on_sale ? " · Sale" : ""}</p>
              </div>
              <Link to={`/admin/products/${p.id}`} className="p-2.5 rounded-lg hover:bg-white/10" aria-label="Edit" data-testid={`admin-product-edit-${p.slug}`}><Pencil size={16} /></Link>
              <button className="p-2.5 rounded-lg hover:bg-white/10" aria-label="Duplicate" onClick={() => act(() => api.post(`/admin/products/${p.id}/duplicate`), "Duplicated (unpublished)")} data-testid={`admin-product-duplicate-${p.slug}`}><Copy size={16} /></button>
              <button className="p-2.5 rounded-lg hover:bg-white/10 text-red-400" aria-label="Delete" data-testid={`admin-product-delete-${p.slug}`}
                onClick={() => {
                  if (!p.archived) return window.confirm("Archive this product? It will be hidden from the store.") && act(() => api.delete(`/admin/products/${p.id}`), "Archived");
                  window.confirm("Permanently delete this product?") && act(() => api.delete(`/admin/products/${p.id}?hard=true`), "Deleted");
                }}><Trash2 size={16} /></button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const EMPTY = { name: "", brand: "", category: "Sneakers", gender: "Unisex", description: "", price: "", sale_price: "", colour: "", sku: "",
  images: [], colours: [], variants: [], featured: false, new_arrival: true, on_sale: false, published: true, archived: false };

function LegacyVariants({ variants, onChange }) {
  const [size, setSize] = useState("");
  const add = (s) => s && !variants.some((v) => v.size === s) && onChange([...variants, { size: s, stock: 0 }]);
  return (
    <div data-testid="variant-editor">
      <div className="space-y-2">
        {variants.map((v, i) => (
          <div key={v.size} className="flex items-center gap-2" data-testid={`variant-row-${v.size.toLowerCase().replace(/\s+/g, "-")}`}>
            <span className="w-24 font-bold text-sm">{v.size}</span>
            <input type="number" min="0" className="field !w-28" value={v.stock} aria-label={`Stock for ${v.size}`} data-testid={`variant-stock-${v.size.toLowerCase().replace(/\s+/g, "-")}`}
              onChange={(e) => onChange(variants.map((x, j) => (j === i ? { ...x, stock: Math.max(0, parseInt(e.target.value || "0", 10)) } : x)))} />
            <span className={`text-xs ${v.stock ? "text-zinc-400" : "text-red-400"}`}>{v.stock ? "available" : "SOLD OUT"}</span>
            <button type="button" onClick={() => onChange(variants.filter((_, j) => j !== i))} className="ml-auto p-2 text-red-400" aria-label="Remove size"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 mt-4">
        <input className="field !w-32" placeholder="e.g. UK 12" value={size} onChange={(e) => setSize(e.target.value)} data-testid="variant-new-size-input" />
        <button type="button" className="chip" onClick={() => { add(size.trim()); setSize(""); }} data-testid="variant-add-button">Add size</button>
        <button type="button" className="chip" onClick={() => onChange([...variants, ...UK_SIZES.filter((s) => !variants.some((v) => v.size === s)).map((s) => ({ size: s, stock: 0 }))])} data-testid="variant-add-uk-range">Add UK 3–11</button>
      </div>
    </div>
  );
}

export function ProductEditor() {
  const { id } = useParams();
  const isNew = id === "new";
  const nav = useNavigate();
  const cats = useFetch("/categories");
  const [f, setF] = useState(isNew ? EMPTY : null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!isNew) api.get(`/admin/products/${id}`).then((r) => setF({ ...r.data, colours: r.data.colours || [], sale_price: r.data.sale_price ?? "" })).catch((e) => toast.error(errMsg(e)));
  }, [id, isNew]);
  if (!f) return <p className="text-zinc-400">Loading...</p>;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    const body = { ...f, price: parseFloat(f.price), sale_price: f.sale_price === "" ? null : parseFloat(f.sale_price) };
    try {
      if (isNew) await api.post("/admin/products", body); else await api.put(`/admin/products/${id}`, body);
      toast.success("Product saved"); nav("/admin/products");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const addColour = (c) => setF({ ...f, colours: [...f.colours, c],
    variants: f.colours.length ? f.variants : f.variants.map((v) => ({ ...v, colour: c.name })) });
  const removeColour = (name) => {
    if (!window.confirm(`Remove ${name}? Its sizes and stock will be removed too.`)) return;
    setF({ ...f, colours: f.colours.filter((c) => c.name !== name), variants: f.variants.filter((v) => v.colour !== name),
      images: f.images.map((im) => (im.colour === name ? { ...im, colour: null } : im)) });
  };
  const input = (k, l, type = "text", extra = {}) => (
    <div><label className="label">{l}</label><input type={type} className="field" value={f[k] ?? ""} onChange={set(k)} data-testid={`product-form-${k.replace("_", "-")}`} {...extra} /></div>
  );
  return (
    <form onSubmit={save} data-testid="product-form">
      <AdminTitle title={isNew ? "New Product" : "Edit Product"}>
        <Link to="/admin/products" className="btn-outline !min-h-[44px]">Cancel</Link>
        <button disabled={busy} className="btn-gold !min-h-[44px]" data-testid="product-form-save">{busy ? "Saving..." : "Save product"}</button>
      </AdminTitle>
      <div className="grid xl:grid-cols-[1fr_380px] gap-6">
        <div className="space-y-6">
          <section className="card-dark p-5 grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">{input("name", "Product name", "text", { required: true })}</div>
            {input("brand", "Brand", "text", { required: true })}
            <div><label className="label">Category</label>
              <select className="field" value={f.category} onChange={set("category")} data-testid="product-form-category">{(cats.data || []).map((c) => <option key={c.id}>{c.name}</option>)}</select></div>
            <div><label className="label">Gender</label>
              <select className="field" value={f.gender} onChange={set("gender")} data-testid="product-form-gender">{["Men", "Women", "Unisex", "Kids"].map((g) => <option key={g}>{g}</option>)}</select></div>
            {!f.colours.length && input("colour", "Colour (single colour product)")}
            {input("price", "Regular price (R)", "number", { required: true, min: 1, step: "0.01" })}
            {input("sale_price", "Sale price (R)", "number", { min: 0, step: "0.01" })}
            {input("sku", "SKU")}
            {!isNew && input("slug", "URL slug")}
          </section>
          <section className="card-dark p-5"><h2 className="font-display text-3xl mb-1">Colour options</h2><p className="text-xs text-zinc-500 mb-4">Add each colour this product comes in. Then tag every photo with its colour and set sizes & stock per colour.</p>
            <ColourEditor colours={f.colours} onAdd={addColour} onRemove={removeColour} /></section>
          <section className="card-dark p-5"><h2 className="font-display text-3xl mb-4">Photos</h2><ImageManager images={f.images} colours={f.colours} onChange={(images) => setF({ ...f, images })} /></section>
        </div>
        <div className="space-y-6">
          <section className="card-dark p-5"><h2 className="font-display text-3xl mb-1">Sizes & stock</h2><p className="text-xs text-zinc-500 mb-4">Stock per size{f.colours.length ? " for each colour" : ""}. Sizes with 0 show as SOLD OUT.</p>
            <ColourVariants variants={f.variants} colours={f.colours} basePrice={f.price} images={f.images}
              onAddImages={(added) => setF((x) => ({ ...x, images: [...x.images, ...added] }))} onChange={(variants) => setF({ ...f, variants })} /></section>
          <section className="card-dark p-5">
            <h2 className="font-display text-3xl mb-2">Visibility</h2>
            <Toggle label="Published (visible in store)" checked={f.published} onChange={(v) => setF({ ...f, published: v })} testid="product-form-published" />
            <Toggle label="Featured" checked={f.featured} onChange={(v) => setF({ ...f, featured: v })} testid="product-form-featured" />
            <Toggle label="New arrival" checked={f.new_arrival} onChange={(v) => setF({ ...f, new_arrival: v })} testid="product-form-new-arrival" />
            <Toggle label="On sale (uses sale price)" checked={f.on_sale} onChange={(v) => setF({ ...f, on_sale: v })} testid="product-form-on-sale" />
            {!isNew && <Toggle label="Archived" checked={f.archived} onChange={(v) => setF({ ...f, archived: v })} testid="product-form-archived" />}
          </section>
        </div>
      </div>
    </form>
  );
}
